import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpClient } from "@angular/common/http";
import { IAuthResponse, IChildrenResponse } from "../model/auth.response.interface";
import { State } from "../model/state.model";
import { User } from "../model/user.model";
import { AppConfig, RequestsType } from '../model/appConfig';
import { environment } from '../../environments/environment';
import { IScheduler } from '../model/scheduler.interface';
import { IPlaylist } from '../model/playlist.interface';

export interface IEmbeddedSchedulerPermissions {
    canControlSchedulerSwitch: boolean;
    canEditWeeklyScheduler: boolean;
    canEditSpecialDates: boolean;
}

@Injectable()
export class ApiService {
    public static readonly baseUrl: string = environment.apiBaseUrl;

    constructor(
        private readonly http: HttpClient,
    ) {
    }

    public authenticate(name: string, password: string): Observable<IAuthResponse> {
        return this.sendRequest(`/MyDjPlayer/Login?User=${name}&Password=${password}`);
    }

    public getChildUsers(name: string, password: string): Observable<IChildrenResponse[]> {
        return this.sendRequest(`/MyDjPlayer/GetChildUsers?User=${name}&Password=${password}`);
    }

    public sendCommand(user: User, command: string): Observable<State[]> {
        return this.sendRequest(`/MyDjPlayer/APIPhonePlayer?User=${user.name}&Password=${user.password}&Command=${encodeURIComponent(command)}`);
    }

    public sendCommandPost(user: User, command: string): Observable<State[]> {
        return this.http.post<State[]>(ApiService.baseUrl + '/MyDjPlayer/APIPhonePlayer', {
            user: user.name,
            password: user.password,
            command
        });
    }

    private sendRequest(url: string): Observable<any> {
        return this.http.jsonp(ApiService.baseUrl + url, 'callback');
    }

    public getEmbeddedScheduler(token: string): Observable<IScheduler> {
        return this.http.get<IScheduler>(ApiService.baseUrl + '/api/scheduler/embedded', {
            headers: { Authorization: `Bearer ${token}` }
        });
    }

    public setEmbeddedScheduler(token: string, scheduler: IScheduler): Observable<{ success: boolean }> {
        return this.http.put<{ success: boolean }>(ApiService.baseUrl + '/api/scheduler/embedded', scheduler, {
            headers: { Authorization: `Bearer ${token}` }
        });
    }

    public getEmbeddedSchedulerPermissions(token: string): Observable<IEmbeddedSchedulerPermissions> {
        return this.http.get<IEmbeddedSchedulerPermissions>(ApiService.baseUrl + '/api/scheduler/embedded/permissions', {
            headers: { Authorization: `Bearer ${token}` }
        });
    }

    public getEmbeddedPlaylists(token: string): Observable<IPlaylist[]> {
        return this.http.get<IPlaylist[]>(ApiService.baseUrl + '/api/scheduler/embedded/playlists', {
            headers: { Authorization: `Bearer ${token}` }
        });
    }

    public loadAppConfig(): Observable<AppConfig> {
        return this.http.get<{requestsType: string}>('/assets/config.json').pipe(
            map(response => ({
                requestsType: this.convertToRequestsType(response.requestsType)
            }))
        );
    }

    private convertToRequestsType(value: string): RequestsType {
        switch (value.toLowerCase()) {
            case 'http':
                return RequestsType.Http;
            case 'signalr':
                return RequestsType.SignalR;
            default:
                return RequestsType.Http; // default fallback
        }
    }
}
